import {
  ApiChartHaving,
  ApiChartRank,
  ApiChartRankDrection,
  ApiMetric,
  ApiOperator,
} from "@incendium/api";

export type ProductPerformanceTab =
  | "top_sellers"
  | "high_ltv"
  | "popular_with_returning_customers"
  | "high_margin"
  | "rising_stars";

export type ProductCardLeadMetric = "primary" | "revenue_growth";

export interface ProductPerformanceTabConfig {
  label: string;
  tabTooltip: string;
  carouselTitle: string;
  carouselTooltip: string;
  orderBy: string;
  primaryMetric: ApiMetric;
  trendMetric: ApiMetric;
  leadMetric?: ProductCardLeadMetric;
  useComparison?: boolean;
  rankByRevenueGrowth?: boolean;
  fetchLimit?: number;
  carouselLimit?: number;
  ranks?: ApiChartRank[];
  having: ApiChartHaving[];
  snapshotFields: ApiMetric[];
  tableFields: ApiMetric[];
}

const BASE_SNAPSHOT_FIELDS = [
  ApiMetric.METRIC_PRODUCT_REVENUE,
  ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
  ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
  ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT,
  ApiMetric.METRIC_SALE_ITEMS_QUANTITY,
  ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
] as const;

const BASE_TABLE_FIELDS = [...BASE_SNAPSHOT_FIELDS] as ApiMetric[];

export const PRODUCT_PERFORMANCE_TABS: Record<
  ProductPerformanceTab,
  ProductPerformanceTabConfig
> = {
  top_sellers: {
    label: "Top Sellers",
    tabTooltip:
      "Your best-selling products in the selected dates — ranked by total sales revenue.",
    carouselTitle: "Top Performing Products",
    carouselTooltip:
      "Products ranked by revenue in the selected period, with margin and conversion from analytics.",
    orderBy: `${ApiMetric.METRIC_PRODUCT_REVENUE} DESC`,
    primaryMetric: ApiMetric.METRIC_PRODUCT_REVENUE,
    trendMetric: ApiMetric.METRIC_PRODUCT_REVENUE,
    having: [
      {
        metric: ApiMetric.METRIC_PRODUCT_REVENUE,
        operator: ApiOperator.IS_GREATER_THAN,
        value: 0,
      },
    ],
    snapshotFields: [...BASE_SNAPSHOT_FIELDS],
    tableFields: [...BASE_TABLE_FIELDS],
  },
  high_ltv: {
    label: "High LTV",
    tabTooltip:
      "Products that attract customers who go on to spend more with you over time — not just on their first order.",
    carouselTitle: "High LTV Products",
    carouselTooltip:
      "Products ranked by gateway LTV — total revenue from customers whose first purchase included this product.",
    orderBy: `${ApiMetric.METRIC_PRODUCT_GATEWAY_LTV} DESC`,
    primaryMetric: ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
    trendMetric: ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
    having: [
      {
        metric: ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
        operator: ApiOperator.IS_GREATER_THAN,
        value: 0,
      },
    ],
    snapshotFields: [
      ApiMetric.METRIC_PRODUCT_GATEWAY_LTV,
      ...BASE_SNAPSHOT_FIELDS,
    ],
    tableFields: [ApiMetric.METRIC_PRODUCT_GATEWAY_LTV, ...BASE_TABLE_FIELDS],
  },
  popular_with_returning_customers: {
    label: "Repeat Buyers",
    tabTooltip:
      "Products your returning customers buy most often — useful for loyalty offers and repeat-purchase campaigns.",
    carouselTitle: "Top Repeat Buyer Products",
    carouselTooltip:
      "Products that repeat customers buy most often, ranked by repeat purchase rate and volume.",
    orderBy: `${ApiMetric.METRIC_RANK} DESC`,
    primaryMetric: ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
    trendMetric: ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
    ranks: [
      {
        metric: ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
        direction: ApiChartRankDrection.DESC,
        weight: 0.8,
      },
      {
        metric: ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
        direction: ApiChartRankDrection.DESC,
        weight: 0.2,
      },
    ],
    having: [
      {
        metric: ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
        operator: ApiOperator.IS_GREATER_THAN,
        value: 0,
      },
    ],
    snapshotFields: [
      ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
      ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
      ...BASE_SNAPSHOT_FIELDS,
    ],
    tableFields: [
      ApiMetric.METRIC_RETURNING_CUSTOMER_RATE,
      ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
      ...BASE_TABLE_FIELDS,
    ],
  },
  high_margin: {
    label: "High Margin",
    tabTooltip:
      "Products that earn you the most profit per sale — helpful when you want to grow profit, not just sales volume.",
    carouselTitle: "High Margin Products",
    carouselTooltip:
      "Products ranked by average margin % in the selected period.",
    orderBy: `${ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT} DESC`,
    primaryMetric: ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT,
    trendMetric: ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT,
    having: [
      {
        metric: ApiMetric.METRIC_PRODUCT_REVENUE,
        operator: ApiOperator.IS_GREATER_THAN,
        value: 0,
      },
    ],
    snapshotFields: [...BASE_SNAPSHOT_FIELDS],
    tableFields: [...BASE_TABLE_FIELDS],
  },
  rising_stars: {
    label: "Rising Stars",
    tabTooltip:
      "Products whose sales are growing fastest compared with your previous period. Choose a comparison period in the date picker to use this view.",
    carouselTitle: "Rising Star Products",
    carouselTooltip:
      "Products with the strongest revenue growth vs the comparison period. Set a comparison period in the date picker.",
    orderBy: `${ApiMetric.METRIC_PRODUCT_REVENUE} DESC`,
    primaryMetric: ApiMetric.METRIC_PRODUCT_REVENUE,
    trendMetric: ApiMetric.METRIC_PRODUCT_REVENUE,
    leadMetric: "revenue_growth",
    useComparison: true,
    rankByRevenueGrowth: true,
    fetchLimit: 50,
    carouselLimit: 8,
    having: [
      {
        metric: ApiMetric.METRIC_PRODUCT_REVENUE,
        operator: ApiOperator.IS_GREATER_THAN,
        value: 0,
      },
    ],
    snapshotFields: [...BASE_SNAPSHOT_FIELDS],
    tableFields: [...BASE_TABLE_FIELDS],
  },
};

export function getProductPerformanceTabConfig(
  tab: ProductPerformanceTab,
): ProductPerformanceTabConfig {
  return PRODUCT_PERFORMANCE_TABS[tab];
}

export function buildProductPerformanceChartFields(
  tabConfig: ProductPerformanceTabConfig,
): ApiMetric[] {
  if (!tabConfig.ranks?.length) {
    return tabConfig.tableFields;
  }

  const rankMetrics = Array.from(
    new Set(tabConfig.ranks.map((rank) => rank.metric as ApiMetric)),
  );

  return [
    ApiMetric.METRIC_RANK,
    ...rankMetrics,
    ...tabConfig.tableFields.filter((field) => !rankMetrics.includes(field)),
  ];
}
